import React from "react";
import ModalBaseView from "../../../../common-deprecated/components/modals/base/ModalBaseView";
import * as Modal from "../../../../common-deprecated/styles/toyota/Modal";
import IconRemove from "../../../../common-deprecated/svg/toyota/remove.svg";
import StatusModalEntry, {
    type StatusEntryType,
} from "../../../../common-deprecated/components/modals/status/StatusModalEntry";
import type { MODAL_TRADE_IN_AEM_DEBUG } from "../../../utils/modalConstants";
import { useTradeInCsl } from "../../../csl/tradeInCslUtils";
import { useTradeInAemSelector } from "../../../redux/store";
import { EnvironmentEnum, formatObjectToJson } from "../../../../common-deprecated/utils";
import { TradeInAemSearchType } from "../../../utils/constants";
import useFindVehicleSearch from "../../../hooks/useFindVehicleSearch";
import IconWrapper from "../../../../common-deprecated/styles/toyota/IconWrapper";
import type { ModalViewType } from "../../../../common-deprecated/types/CommonTypes";

export type TradeInAemDebugModalType = {
    type: typeof MODAL_TRADE_IN_AEM_DEBUG;
};

const TradeInAemDebugModal = (props: ModalViewType<TradeInAemDebugModalType>): JSX.Element => {
    const { show, close, modalSettings } = props;
    const csl = useTradeInCsl();
    const commonSettings = useTradeInAemSelector((state) => state.commonSettings);
    const { setSearchValue } = useFindVehicleSearch(TradeInAemSearchType.LicensePlate);

    const vehicle = useTradeInAemSelector((state) => state.tradeInAemVehicle.vehicle);
    const eHub = useTradeInAemSelector((state) => state.tradeInAemUtils.eHub);
    const vehicleValue = useTradeInAemSelector((state) => state.tradeInAemVehicleValue);

    const statusEntries: StatusEntryType[] = [
        // Valuation Response
        {
            title: "Valuation Data",
            values: [
                {
                    label: "Request successful",
                    value: vehicleValue.latestResponse ? !vehicleValue.failed : "N/A",
                },
                {
                    label: "Payload (Current vehicle data)",
                    collapsibleContent: vehicle
                        ? formatObjectToJson(vehicle)
                        : "No vehicle data available yet. Finish step 1 first",
                },
                {
                    label: "Response",
                    collapsibleContent: vehicleValue.latestResponse
                        ? formatObjectToJson(vehicleValue.latestResponse)
                        : "No valuation data available yet. Finish valuation first",
                },
            ],
        },
        // EHub Data
        {
            title: "Ehub Data",
            values: [
                { label: "Request successful", value: eHub.latestResponse ? !eHub.failed : "N/A" },
                {
                    label: "Payload",
                    collapsibleContent: vehicle
                        ? formatObjectToJson(csl.getEHubPayload())
                        : "No EHub data available yet. Finish step 1 first",
                },
                {
                    label: "Response",
                    collapsibleContent: eHub.latestResponse
                        ? formatObjectToJson(eHub.latestResponse)
                        : "No EHub response data available yet.",
                },
            ],
        },
    ];

    // Test Plates & VINs (Hide on production)
    if (commonSettings.environment !== EnvironmentEnum.Production) {
        statusEntries.push({
            title: "Test plates",
            description: csl.testPlates.length === 0 ? "No test plates provided" : "",
            values: csl.testPlates.map(({ description, value }) => ({
                label: description,
                value,
                onClick: () => {
                    setSearchValue(value);
                    csl.fetchVehicleResults(value, TradeInAemSearchType.LicensePlate);
                    close();
                },
            })),
        });

        statusEntries.push({
            title: "Test VINs",
            description: csl.testVins.length === 0 ? "No test VINs provided" : "",
            values: csl.testVins.map(({ description, value }) => ({
                label: description,
                value,
                onClick: () => {
                    setSearchValue(value);
                    csl.fetchVehicleResults(value, TradeInAemSearchType.VinNumber);
                    close();
                },
            })),
        });
    }

    return (
        <ModalBaseView modalIdType={modalSettings.type} onClose={close} show={show} size="md">
            <Modal.Close onClick={close} small>
                <IconWrapper iconColor="iconNeutral">
                    <IconRemove />
                </IconWrapper>
            </Modal.Close>
            <Modal.Header>
                <Modal.Title textAlign="left">Trade In Aem Debug</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {statusEntries.map((entry, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                        <StatusModalEntry entry={entry} />
                    </React.Fragment>
                ))}
            </Modal.Body>
        </ModalBaseView>
    );
};

export default TradeInAemDebugModal;
